// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useCredits } from '../contexts/CreditsContext';
import { upgradesConfig } from '../config/UpgradesConfig';
import { weaponsConfig } from '../config/WeaponsConfig';
import { shipModules } from '../config/ShipModulesConfig';
import { tradeGoods } from '../config/TradeGoodsConfig';
import { crewRoles, EXPERIENCE_LEVELS, calculateCrewCost } from '../config/CrewHiringConfig';
import './UniversalShop.css';

/**
 * Universal Shop Component
 * Replaces: TradeShop, WeaponShop, UpgradeShop, ModulesShop, ArmoryModal, ResourcesShop
 */
const UniversalShop = ({
  type, // 'trade' | 'weapons' | 'upgrades' | 'modules' | 'resources' | 'crew'
  title,
  items: itemsProp,
  onPurchase,
  onSell,
  onClose,
  inventory = null, // For trade goods
  canSell = false,
  embedded = false,
  location = null
}) => {
  const { credits } = useCredits(); // Get credits from context
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [experienceLevel, setExperienceLevel] = useState('TRAINED'); // For crew hiring
  const [modalFading, setModalFading] = useState(false);

  // Load items based on type if not provided
  const items = useMemo(() => {
    if (itemsProp) return itemsProp;
    
    switch (type) {
      case 'upgrades': {
        // Flatten all upgrade categories and normalize to have 'price' field
        const allUpgrades = [];
        Object.values(upgradesConfig).forEach(category => {
          if (Array.isArray(category)) {
            category.forEach(item => {
              allUpgrades.push({ ...item, price: item.cost });
            });
          }
        });
        return allUpgrades;
      }
      case 'weapons': {
        // Convert weaponsCatalog to array and normalize to have 'price' field
        return Object.values(weaponsConfig.weaponsCatalog || {}).map(weapon => ({
          ...weapon,
          price: weapon.cost
        }));
      }
      case 'modules': {
        // Convert modules to array and normalize to have 'price' field
        return Object.values(shipModules).map(module => ({
          ...module,
          price: module.cost
        }));
      }
      case 'trade': {
        // Convert trade goods to array and use basePrice as price
        return Object.values(tradeGoods).map(good => ({
          ...good,
          price: good.basePrice
        }));
      }
      case 'resources': {
        // Basic ship resources (food, water, oxygen, medical, parts)
        return [
          { id: 'food', name: 'Food Supplies', icon: '🍖', description: 'Nutritional rations for crew', price: 10, basePrice: 10 },
          { id: 'water', name: 'Water', icon: '💧', description: 'Purified water for crew consumption', price: 5, basePrice: 5 },
          { id: 'oxygen', name: 'Oxygen', icon: '🫧', description: 'Breathable oxygen for life support', price: 8, basePrice: 8 },
          { id: 'medical', name: 'Medical Supplies', icon: '💊', description: 'First aid and medical equipment', price: 20, basePrice: 20 },
          { id: 'parts', name: 'Spare Parts', icon: '🔧', description: 'Replacement components for repairs', price: 15, basePrice: 15 }
        ];
      }
      case 'crew':
        return crewRoles;
      default:
        return [];
    }
  }, [type, itemsProp]);

  const handleClose = () => {
    setModalFading(true);
    setTimeout(onClose, 300);
  };

  const handlePurchase = (item, qty = 1) => {
    const itemPrice = getItemPrice(item);
    const totalCost = itemPrice * qty;
    if (credits >= totalCost) {
      // For crew, pass experience level
      if (type === 'crew') {
        onPurchase(item, qty, experienceLevel);
      } else {
        onPurchase(item, qty);
      }
      setSelectedItem(null);
      setQuantity(1);
      setExperienceLevel('TRAINED');
    }
  };

  const handleSell = (item, qty = 1) => {
    if (onSell) {
      onSell(item, qty);
      setSelectedItem(null);
      setQuantity(1);
    }
  };

  const getItemIcon = (item) => {
    if (item.icon) return item.icon;
    
    // Default icons by type
    const icons = {
      trade: '📦',
      weapons: '⚔️',
      upgrades: '⚙️',
      modules: '🔧',
      resources: '🛢️'
    };
    return icons[type] || '•';
  };

  const canAfford = (item, qty = 1) => {
    const itemPrice = type === 'crew' 
      ? calculateCrewCost(item.basePrice, experienceLevel)
      : item.price;
    return credits >= (itemPrice * qty);
  };

  const getItemPrice = (item) => {
    if (type === 'crew') {
      return calculateCrewCost(item.basePrice, experienceLevel);
    }
    return item.price;
  };

  const content = (
    <div className="universal-shop-modal" onClick={(e) => e.stopPropagation()}>
      
      {/* Header */}
      <div className="shop-header">
        <h2 className="shop-title">{title || `${type.toUpperCase()} SHOP`}</h2>
        <div className="shop-credits">
          <span className="credits-label">Credits:</span>
          <span className="credits-value">{credits?.toLocaleString() || 0}¢</span>
        </div>
        {!embedded && <button className="shop-close" onClick={handleClose}>✕</button>}
      </div>

      {/* Items Grid */}
      <div className="shop-content">
        <div className="shop-items-grid">
          {items.map((item, idx) => (
              <div
                key={idx}
                className={`shop-item ${selectedItem?.id === item.id ? 'selected' : ''} ${!canAfford(item) ? 'unaffordable' : ''}`}
                onClick={() => setSelectedItem(item)}
              >
                <div className="item-icon">{getItemIcon(item)}</div>
                <div className="item-info">
                  <div className="item-name">{item.name}</div>
                  {item.description && (
                    <div className="item-description">{item.description}</div>
                  )}
                  <div className="item-price">
                    {item.price.toLocaleString()}¢
                  </div>
                  {item.owned && (
                    <div className="item-owned">✓ Owned</div>
                  )}
                  {inventory && inventory[item.id] && (
                    <div className="item-stock">Stock: {inventory[item.id]}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Item Details Panel */}
          {selectedItem && (
            <div className="shop-details-panel">
              <div className="details-header">
                <div className="details-icon">{getItemIcon(selectedItem)}</div>
                <div className="details-title">{selectedItem.name}</div>
              </div>
              
              {selectedItem.description && (
                <div className="details-description">{selectedItem.description}</div>
              )}

              {selectedItem.stats && (
                <div className="details-stats">
                  {Object.entries(selectedItem.stats).map(([key, value]) => (
                    <div key={key} className="stat-row">
                      <span className="stat-label">{key}:</span>
                      <span className="stat-value">{value}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="details-price">
                Price: <span className="price-value">{getItemPrice(selectedItem).toLocaleString()}¢</span>
              </div>

              {/* Experience Level Selector (for crew hiring) */}
              {type === 'crew' && (
                <div className="experience-selector">
                  <div className="selector-label">Experience Level:</div>
                  <div className="experience-options">
                    {Object.values(EXPERIENCE_LEVELS).map(exp => (
                      <button
                        key={exp.id}
                        className={`exp-option ${experienceLevel === exp.id ? 'selected' : ''}`}
                        onClick={() => setExperienceLevel(exp.id)}
                      >
                        <div className="exp-name">{exp.name}</div>
                        <div className="exp-multiplier">{exp.multiplier}x</div>
                        <div className="exp-cost">{calculateCrewCost(selectedItem.basePrice, exp.id)}¢</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity Selector (for stackable items) */}
              {type === 'trade' || type === 'resources' ? (
                <div className="quantity-selector">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
                  <input 
                    type="number" 
                    value={quantity} 
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                  />
                  <button onClick={() => setQuantity(quantity + 1)}>+</button>
                  <button onClick={() => setQuantity(10)}>+10</button>
                  <button onClick={() => setQuantity(50)}>+50</button>
                </div>
              ) : null}

              {/* Action Buttons */}
              <div className="details-actions">
                <button
                  className="btn-purchase"
                  onClick={() => handlePurchase(selectedItem, quantity)}
                  disabled={!canAfford(selectedItem, quantity) || selectedItem.owned}
                >
                  {selectedItem.owned ? 'OWNED' : `BUY ${quantity > 1 ? `(${quantity})` : ''}`}
                </button>
                
                {canSell && inventory && inventory[selectedItem.id] > 0 && (
                  <button
                    className="btn-sell"
                    onClick={() => handleSell(selectedItem, quantity)}
                  >
                    SELL {quantity > 1 ? `(${quantity})` : ''}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
  );

  // Render embedded or as modal
  if (embedded) {
    return content;
  }

  return createPortal(
    <div className={`universal-shop-overlay ${modalFading ? 'fading' : ''}`} onClick={handleClose}>
      {content}
    </div>,
    document.body
  );
};

export default UniversalShop;
